import { type FC, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdCalendarToday, MdAlarm, MdLabel } from 'react-icons/md';
import { format, parseISO } from 'date-fns';
import type { Task } from '../types';
import { useTheme } from '../hooks/useTheme';

interface TaskDetailModalProps {
  task: Task | null;
  onClose: () => void;
}

const TaskDetailModal: FC<TaskDetailModalProps> = ({ task, onClose }) => {
  const { theme } = useTheme();

  useEffect(() => {
    // Prevent body scroll when modal is open
    if (task) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [task]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (task) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [task, onClose]);

  if (!task) return null;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return theme === 'dark' ? '#ec4899' : '#db2777';
      case 'medium': return 'var(--color-theme)';
      case 'low': return theme === 'dark' ? '#10b981' : '#059669';
      default: return theme === 'dark' ? '#6b7280' : '#9ca3af';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return theme === 'dark' ? '#10b981' : '#059669';
      case 'in-progress': return theme === 'dark' ? '#f59e0b' : '#d97706';
      case 'pending': return theme === 'dark' ? '#6b7280' : '#9ca3af';
      default: return theme === 'dark' ? '#6b7280' : '#9ca3af';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[9998] flex items-center justify-center p-4 backdrop-blur-sm"
        style={{
          backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl border-2"
          style={{
            backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
            borderColor: 'var(--color-theme)'
          }}
        >
          {/* Header */}
          <div 
            className="sticky top-0 z-10 flex items-start justify-between p-6 border-b-2"
            style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
              borderColor: 'var(--color-theme)'
            }}
          >
            <div className="flex-1 pr-4">
              <h2 
                className="text-2xl font-bold mb-2 break-words"
                style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
              >
                {task.title}
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <span 
                  className="px-3 py-1 rounded-full text-xs font-semibold uppercase"
                  style={{
                    backgroundColor: `${getPriorityColor(task.priority)}20`,
                    color: getPriorityColor(task.priority)
                  }}
                >
                  {task.priority} Priority
                </span>
                <span 
                  className="px-3 py-1 rounded-full text-xs font-semibold uppercase"
                  style={{
                    backgroundColor: `${getStatusColor(task.status)}20`,
                    color: getStatusColor(task.status)
                  }}
                >
                  {task.status.replace('-', ' ')}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close modal"
            >
              <MdClose 
                className="w-6 h-6"
                style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
              />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Description */}
            {task.description && (
              <div>
                <h3 
                  className="text-sm font-semibold mb-2 uppercase tracking-wide"
                  style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                >
                  Description
                </h3>
                <p 
                  className="text-base leading-relaxed whitespace-pre-wrap"
                  style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}
                >
                  {task.description}
                </p>
              </div>
            )}

            {/* Task Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Due Date */}
              {task.due_date && (
                <div 
                  className="p-4 rounded-lg border-2"
                  style={{
                    backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb',
                    borderColor: theme === 'dark' ? '#374151' : '#e5e7eb'
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MdCalendarToday className="w-5 h-5" style={{ color: 'var(--color-theme)' }} />
                    <h4 
                      className="text-sm font-semibold"
                      style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                    >
                      Due Date
                    </h4>
                  </div>
                  <p 
                    className="text-base font-medium"
                    style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
                  >
                    {format(parseISO(task.due_date), 'MMM d, yyyy h:mm aa')}
                  </p>
                </div>
              )}

              {/* Start Time */}
              {task.start_time && (
                <div 
                  className="p-4 rounded-lg border-2"
                  style={{
                    backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb',
                    borderColor: theme === 'dark' ? '#374151' : '#e5e7eb'
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MdCalendarToday className="w-5 h-5 text-green-500" />
                    <h4 
                      className="text-sm font-semibold"
                      style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                    >
                      Start Time
                    </h4>
                  </div>
                  <p 
                    className="text-base font-medium"
                    style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
                  >
                    {format(parseISO(task.start_time), 'MMM d, yyyy h:mm aa')}
                  </p>
                </div>
              )}

              {/* Alarm Time */}
              {task.alarm_time && (
                <div 
                  className="p-4 rounded-lg border-2"
                  style={{
                    backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb',
                    borderColor: theme === 'dark' ? '#374151' : '#e5e7eb'
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MdAlarm className="w-5 h-5 text-yellow-500" />
                    <h4 
                      className="text-sm font-semibold"
                      style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                    >
                      Alarm
                    </h4>
                  </div>
                  <p 
                    className="text-base font-medium"
                    style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
                  >
                    {format(parseISO(task.alarm_time), 'MMM d, yyyy h:mm aa')}
                  </p>
                </div>
              )}

              {/* Reminder Interval */}
              {task.reminder_interval && (
                <div 
                  className="p-4 rounded-lg border-2"
                  style={{
                    backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb',
                    borderColor: theme === 'dark' ? '#374151' : '#e5e7eb'
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MdAlarm className="w-5 h-5 text-purple-500" />
                    <h4 
                      className="text-sm font-semibold"
                      style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                    >
                      Reminder
                    </h4>
                  </div>
                  <p 
                    className="text-base font-medium"
                    style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
                  >
                    Every {task.reminder_interval} minutes
                  </p>
                </div>
              )}
            </div>

            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MdLabel className="w-5 h-5 text-pink-500" />
                  <h3 
                    className="text-sm font-semibold uppercase tracking-wide"
                    style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                  >
                    Tags
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {task.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 rounded-full text-sm font-medium border-2"
                      style={{
                        backgroundColor: theme === 'dark' ? '#374151' : '#f3f4f6',
                        borderColor: theme === 'dark' ? '#ec4899' : '#ec4899',
                        color: theme === 'dark' ? '#f3f4f6' : '#1f2937'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div 
              className="pt-4 border-t-2 text-xs"
              style={{
                borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280'
              }}
            >
              <div className="flex items-center justify-between">
                <span>Created: {format(parseISO(task.created_at), 'MMM d, yyyy')}</span>
                {task.updated_at && task.updated_at !== task.created_at && (
                  <span>Updated: {format(parseISO(task.updated_at), 'MMM d, yyyy')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div 
            className="sticky bottom-0 flex items-center justify-center p-6 border-t-2"
            style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
              borderColor: 'var(--color-theme)'
            }}
          >
            <button
              onClick={onClose}
              className="px-8 py-3 rounded-lg font-semibold transition-all hover:shadow-lg"
              style={{
                backgroundColor: 'var(--color-theme)',
                color: '#ffffff'
              }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TaskDetailModal;
